import Image from 'next/image'
import Link from 'next/link'
import { getReadableImageUrl } from "@/lib/directus";
import { sanitizeHTML } from "@/lib/utils";

interface ThinHeroData {
  heading?: {
    text?: string;
    title?: string;
  };
  text?: {
    text?: string;
  };
  button?: {
    label?: string;
    href?: string;
  };
  desktop_background?: {
    alt: string;
    image_file: {
      image: {
        id: string;
        filename_download: string;
      };
    };
  };
  mobile_background?: {
    alt: string;
    image_file: {
      image: {
        id: string;
        filename_download: string;
      };
    };
  };
}

export const fields = [
  "heading.text",
  "heading.title",
  
  "text.text",
  
  "button.label",
  "button.href",
  
  "desktop_background.alt",
  "desktop_background.image_file.image.id",
  "desktop_background.image_file.image.filename_download",
  
  "mobile_background.alt",
  "mobile_background.image_file.image.id",
  "mobile_background.image_file.image.filename_download",
] as const;

interface ThinHeroProps {
  data?: ThinHeroData;
}

export default function ThinHero({ data }: ThinHeroProps) {
  // Get desktop background image details
  const desktopBgId = data?.desktop_background?.image_file?.image?.id;
  const desktopBgFilename = data?.desktop_background?.image_file?.image?.filename_download;
  const desktopBgAlt = data?.desktop_background?.alt;
  
  // Get mobile background image details
  const mobileBgId = data?.mobile_background?.image_file?.image?.id;
  const mobileBgFilename = data?.mobile_background?.image_file?.image?.filename_download;
  const mobileBgAlt = data?.mobile_background?.alt;
  
  return (
    <section className="relative w-full h-[360px] md:h-[430px] overflow-hidden bg-[#efefef]">
      {/* Desktop Background */}
      <div className="hidden md:block absolute inset-0 w-full h-full">
        {desktopBgId && desktopBgFilename && (
          <Image
            src={getReadableImageUrl(desktopBgId, desktopBgFilename)}
            alt={desktopBgAlt || ""}
            fill
            loading="lazy"
            quality={65}
            className="object-cover object-left w-full"
            sizes="(max-width: 768px) 0vw, 100vw"
          />
        )}
      </div>

      {/* Mobile Background */}
      <div className="block md:hidden absolute inset-0 w-full h-full">
        {mobileBgId && mobileBgFilename && (
          <Image
            src={getReadableImageUrl(mobileBgId, mobileBgFilename)}
            alt={mobileBgAlt || ""}
            fill
            priority
            fetchPriority="high"
            quality={65}
            className="object-cover"
            sizes="(max-width: 768px) 100vw, 0vw"
          />
        )}
      </div>

      {/* Content Container */}
      <div className="relative h-full container mx-auto">
        <div className="absolute md:left-[77px] md:top-[60px] left-8 bottom-8 md:bottom-auto bg-white/65 rounded-xl p-4 lg:p-8 w-[calc(100%-4rem)] md:w-auto md:max-w-[660px]">
          <h1 className="text-[#193762] text-[2.5rem] font-essonnes font-light leading-tight md:text-[3.625rem] lg:text-[4rem]">
            {data?.heading?.title}
          </h1>
          <p className="font-greycliff text-lg md:text-2xl text-[#193762] mt-4 md:mt-6 tracking-tight leading-normal md:font-medium max-w-[315px] md:max-w-[506px]"
             dangerouslySetInnerHTML={sanitizeHTML(data?.text?.text || "")}
          />
          {data?.button?.href && data?.button?.label && (
            <Link 
              href={data?.button?.href || "#"}
              className="mt-8 md:mt-[37px] inline-flex h-[4.375rem] w-full md:w-fit items-center justify-center rounded-[3.125rem] px-9 text-xl font-normal tracking-tight text-white bg-primary hover:bg-primary/90 text-center"
            >
              <span className="font-greycliff leading-7 text-center">{data?.button?.label}</span>
            </Link>
          )}
        </div>
      </div>
    </section>
  )
} 